
import React from 'react';
import { motion } from 'framer-motion';

interface CategoryData {
  category_name: string;
  profile_content: any;
  processing_status: 'completed' | 'failed';
  error_message?: string;
}

interface AthleteIntelligenceScoreDialProps {
  completedCategories: CategoryData[];
  onScrollToAnalysis: () => void;
  className?: string;
}

const allCategories = [
  'family',
  'financial', 
  'career',
  'personal_life',
  'agent',
  'personality',
  'early_career_and_education',
  'adverse_media',
  'legal_and_criminal_history',
  'sponsorships',
  'on-field_comments'
];

const getAISGradientColors = (score: number) => {
  if (score >= 9) return ['#e7c887', '#22c55e'];
  if (score >= 8) return ['#22c55e', '#65d373'];
  if (score >= 7) return ['#eab308', '#22c55e'];
  if (score >= 6) return ['#eab308', '#fbbf24'];
  if (score >= 5) return ['#f97316', '#eab308'];
  if (score >= 4) return ['#f97316', '#fb923c'];
  if (score >= 3) return ['#ef4444', '#f97316'];
  return ['#dc2626', '#ef4444'];
};

// Create a mapping function to match frontend category names with backend names
const findMatchingCategory = (frontendCategoryName: string, completedCategories: CategoryData[]): CategoryData | undefined => {
  // First try exact match
  let match = completedCategories.find(c => c.category_name === frontendCategoryName);
  if (match) return match;
  
  // Try case-insensitive match
  match = completedCategories.find(c => c.category_name.toLowerCase() === frontendCategoryName.toLowerCase());
  if (match) return match;
  
  // Try fuzzy matching for known mappings
  const mappings: Record<string, string[]> = {
    'family': ['family_and_relationships', 'family'],
    'financial': ['financial'],
    'career': ['career'],
    'personal_life': ['personal_life'],
    'agent': ['agent'],
    'personality': ['personality'],
    'early_career_and_education': ['early_career_and_youth', 'early_career_and_education'],
    'adverse_media': ['adverse_media'],
    'legal_and_criminal_history': ['legal_criminal_or_compliance_issues', 'legal_and_criminal_history'],
    'sponsorships': ['sponsorships_and_commercial', 'sponsorships'],
    'on-field_comments': ['on_field_comments', 'on-field_comments']
  };
  
  const possibleNames = mappings[frontendCategoryName] || [];
  
  for (const possibleName of possibleNames) {
    match = completedCategories.find(c => 
      c.category_name.toLowerCase() === possibleName.toLowerCase() ||
      c.category_name.toLowerCase().replace(/[_-]/g, '') === possibleName.toLowerCase().replace(/[_-]/g, '')
    );
    if (match) return match;
  }
  
  // Last resort: partial matching
  const normalizedFrontend = frontendCategoryName.toLowerCase().replace(/[_-]/g, '');
  match = completedCategories.find(c => {
    const normalizedBackend = c.category_name.toLowerCase().replace(/[_-]/g, '');
    return normalizedBackend.includes(normalizedFrontend.split('_')[0]) || 
           normalizedFrontend.includes(normalizedBackend.split('_')[0]);
  });
  
  return match;
};

export const AthleteIntelligenceScoreDial: React.FC<AthleteIntelligenceScoreDialProps> = ({
  completedCategories,
  onScrollToAnalysis,
  className = ''
}) => {
  // Calculate scores for each category
  const categoryScores = allCategories.map(categoryName => {
    const completedCategory = findMatchingCategory(categoryName, completedCategories);
    let rating = 0;
    
    if (completedCategory?.profile_content) {
      try {
        // Check if profile_content is already an object or needs parsing
        const profileContent = typeof completedCategory.profile_content === 'string' 
          ? JSON.parse(completedCategory.profile_content)
          : completedCategory.profile_content;
        rating = profileContent?.rating?.score || 0;
      } catch (e) {
        console.error('Error parsing profile content:', e, 'Raw content:', completedCategory.profile_content);
      }
    }
    
    return {
      name: categoryName,
      score: rating,
      isCompleted: !!completedCategory
    };
  });
  
  // Calculate AIS (average of completed categories)
  const completedScores = categoryScores.filter(c => c.isCompleted && c.score > 0);
  const aisScore = completedScores.length > 0 
    ? completedScores.reduce((sum, c) => sum + c.score, 0) / completedScores.length 
    : 0;
  
  // Don't render if no categories are completed
  if (completedScores.length === 0) {
    return (
      <div className={`flex flex-col items-center justify-center bg-transparent rounded-lg p-4 border border-gray-500 w-[200px] ${className}`}>
        <div className="text-center">
          <div className="text-sm text-[#CECFD2] mb-2">Athlete Intelligence Score</div>
          <div className="text-xs text-gray-400">Analyzing...</div>
        </div>
      </div>
    );
  }
  
  const radius = 60;
  const strokeWidth = 8;
  const circumference = 2 * Math.PI * radius;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (aisScore / 10) * circumference;
  const gradientColors = getAISGradientColors(aisScore);
  
  return (
    <motion.div 
      className={`flex flex-col items-center justify-center bg-transparent rounded-lg p-4 border border-gray-500 w-[200px] hover:bg-[#26222E] transition-colors duration-200 cursor-pointer group ${className}`}
      onClick={onScrollToAnalysis}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="relative">
        <svg width="140" height="140" className="transform -rotate-90">
          <defs>
            <linearGradient id="aisGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={gradientColors[0]} />
              <stop offset="100%" stopColor={gradientColors[1]} />
            </linearGradient>
          </defs>
          
          {/* Background circle */}
          <circle
            cx="70"
            cy="70"
            r={radius}
            stroke="#22333B"
            strokeWidth={strokeWidth}
            fill="transparent"
            opacity="0.3"
          />
          
          {/* Progress circle */}
          <circle
            cx="70"
            cy="70"
            r={radius}
            stroke="url(#aisGradient)"
            strokeWidth={strokeWidth}
            fill="transparent"
            strokeLinecap="round"
            strokeDasharray={strokeDasharray}
            strokeDashoffset={strokeDashoffset}
            className="transition-all duration-1000 ease-out"
          />
        </svg>
        
        {/* Center content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-3xl font-bold text-[#C6AC8E] group-hover:text-[#e7c887] transition-colors">
            {Math.round(aisScore * 100)}
          </div>
          <div className="text-xs text-[#5E503F] group-hover:text-[#C6AC8E] transition-colors">
            AIS
          </div>
        </div>
      </div>
      
      <div className="mt-2 text-xs text-[#CECFD2] text-center">
        <p>Athlete Intelligence Score</p>
        <p className="text-[#5E503F]">Based on {completedScores.length} categories</p>
      </div>
    </motion.div>
  );
};
